import type { WineSalesMonthly } from "types/fulfillment";

export type WinePieSegment = {
  name: string;
  value: number;
  percent: number;
  color: string;
};

/** Mesma ordem que `WINE_SALES_CATEGORIES` na Cloud Function. */
const TYPE_ORDER = [
  "Tinto",
  "Seco",
  "Branco",
  "Rosé",
  "Espumante",
  "Outros",
] as const;

const COLORS: Record<string, string> = {
  Tinto: "#BE2C55",
  Seco: "#DD6B20",
  Branco: "#39B8FF",
  Rosé: "#D98A9E",
  Espumante: "#7551FF",
  Outros: "#A3AED0",
};

export type WinePiePeriod = "lastMonth" | "last6Months";

/**
 * Agrega valores por tipo a partir da série mensal devolvida por `partnerGetTasks`.
 * Devolve array vazio se não houver vendas no período.
 */
export function winePieSegmentsFromMonthly(
  w: WineSalesMonthly,
  period: WinePiePeriod
): WinePieSegment[] {
  const { series, categories } = w;
  const lastIdx = Math.max(0, (categories?.length ?? 1) - 1);

  const amountByType: Record<string, number> = {};
  for (const name of TYPE_ORDER) {
    amountByType[name] = 0;
  }

  for (const s of series) {
    if (!(s.name in amountByType)) {
      amountByType[s.name] = 0;
    }
    if (period === "lastMonth") {
      amountByType[s.name] += s.data[lastIdx] ?? 0;
    } else {
      amountByType[s.name] += s.data.reduce((a, b) => a + b, 0);
    }
  }

  const total = Object.values(amountByType).reduce((a, b) => a + b, 0);
  if (total <= 0.0001) {
    return [];
  }

  const ordered = TYPE_ORDER.filter((name) => amountByType[name] > 0.0001).map(
    (name) => ({
      name,
      value: Math.round(amountByType[name] * 100) / 100,
      percent: Math.round((amountByType[name] / total) * 1000) / 10,
      color: COLORS[name] ?? "#A3AED0",
    })
  );

  const sumP = ordered.reduce((a, s) => a + s.percent, 0);
  const diff = Math.round((100 - sumP) * 10) / 10;
  if (ordered.length > 0 && Math.abs(diff) >= 0.05) {
    const i = ordered.reduce(
      (best, s, idx, arr) => (s.percent > arr[best].percent ? idx : best),
      0
    );
    ordered[i] = {
      ...ordered[i],
      percent: Math.round((ordered[i].percent + diff) * 10) / 10,
    };
  }

  return ordered.sort((a, b) => b.percent - a.percent);
}
